import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import CloseIcon from '@mui/icons-material/CloseRounded'
import RefreshIcon from '@mui/icons-material/RefreshRounded'
import WarningIcon from '@mui/icons-material/WarningAmberRounded'
import CloudOffIcon from '@mui/icons-material/CloudOffRounded'
import WbSunnyIcon from '@mui/icons-material/WbSunnyRounded'
import { shallow } from 'zustand/shallow'
import useWeatherStore from '../../store/weather'
import type {
  WeatherAlert,
  WeatherAssistantSessionMetadata,
  WeatherDailyEntry,
  WeatherHourlyEntry,
  WeatherState,
} from '../../api/weather'
import { formatRelativeTime } from '../../utils/relativeTime'
import {
  formatStationTime,
  resolveEpochMs,
  resolveStationTimeZone,
  stationDayDiff,
} from '../../utils/time'

export interface WeatherPanelProps {
  open: boolean
  onClose: () => void
  stationId: number | null
  stationName: string | null
  stationTimezone: string | null
  dialogId?: string
}

const WEATHER_SECTION_GAP = 2

const formatTemperature = (value: number | null | undefined, unit: string | null | undefined): string => {
  if (!Number.isFinite(value ?? Number.NaN)) {
    return '—'
  }
  const rounded = Math.round(Number(value))
  if (typeof unit === 'string' && unit.trim().length > 0) {
    return `${rounded}${unit}`
  }
  return `${rounded}°`
}

const formatFeelsLike = (
  feelsLike: number | null | undefined,
  unit: string | null | undefined,
): string => {
  if (!Number.isFinite(feelsLike ?? Number.NaN)) {
    return '—'
  }
  return `${Math.round(Number(feelsLike))}${unit ?? '°'}`
}

const formatPercent = (value: number | null | undefined): string => {
  if (!Number.isFinite(value ?? Number.NaN)) {
    return '—'
  }
  const numeric = Number(value)
  const normalized = numeric > 1 ? numeric : numeric * 100
  return `${Math.round(normalized)}%`
}

const formatWind = (
  speed: number | null | undefined,
  system: string | null | undefined,
  direction: number | null | undefined,
): string => {
  if (!Number.isFinite(speed ?? Number.NaN)) {
    return '—'
  }
  const roundedSpeed = Math.round(Number(speed))
  const suffix = (() => {
    if (typeof system !== 'string') {
      return ' km/h'
    }
    const normalized = system.trim().toLowerCase()
    if (normalized === 'imperial' || normalized === 'us') {
      return ' mph'
    }
    if (normalized === 'metric' || normalized === 'si') {
      return ' km/h'
    }
    if (normalized.includes('mph') || normalized.includes('km') || normalized.includes('m/s')) {
      return ` ${system.trim()}`
    }
    return ` ${system.trim()}`
  })()
  if (Number.isFinite(direction ?? Number.NaN)) {
    return `${roundedSpeed}${suffix} · ${Math.round(Number(direction))}°`
  }
  return `${roundedSpeed}${suffix}`
}

const resolveIconUrl = (icon: string | null | undefined): string | null => {
  if (typeof icon !== 'string' || icon.trim() === '') {
    return null
  }
  const trimmed = icon.trim()
  return `https://openweathermap.org/img/wn/${trimmed}@2x.png`
}

const formatCooldownLabel = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return ''
  }
  const totalSeconds = Math.max(0, Math.ceil(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`
  }
  return `${remainingSeconds}s`
}

const getHourlyLabel = (
  entry: WeatherHourlyEntry,
  stationTimezone: string | null,
): string => {
  const epoch = resolveEpochMs({ isoString: entry.time ?? null })
  if (!Number.isFinite(epoch ?? Number.NaN)) {
    return '—'
  }
  const tz = resolveStationTimeZone(stationTimezone ?? undefined)
  return formatStationTime(epoch as number, tz, { hour: 'numeric', minute: '2-digit' })
}

const getDailyLabel = (
  entry: WeatherDailyEntry,
  stationTimezone: string | null,
): string => {
  const epoch = resolveEpochMs({ isoString: entry.date ?? null })
  if (!Number.isFinite(epoch ?? Number.NaN)) {
    return '—'
  }
  const tz = resolveStationTimeZone(stationTimezone ?? undefined)
  return formatStationTime(epoch as number, tz, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

const renderHourlyCard = (
  entry: WeatherHourlyEntry,
  stationTimezone: string | null,
  index: number,
  temperatureUnit: string,
  windSystem: string | null,
) => {
  const label = getHourlyLabel(entry, stationTimezone)
  const iconUrl = resolveIconUrl(entry.icon)
  const temperatureLabel = formatTemperature(entry.temperature, temperatureUnit)
  const popLabel = formatPercent(entry.pop)
  return (
    <Paper
      key={`${entry.time ?? index}-hourly`}
      role="listitem"
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 120,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography variant="subtitle2" component="p">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={entry.description ?? 'Weather condition'}
            width={48}
            height={48}
            loading="lazy"
          />
        ) : null}
        <Typography variant="h6" component="p">
          {temperatureLabel}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {entry.description ?? '—'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        POP {popLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Wind {formatWind(entry.wind_speed, windSystem, entry.wind_deg)}
      </Typography>
    </Paper>
  )
}

const renderDailyItem = (
  entry: WeatherDailyEntry,
  stationTimezone: string | null,
  index: number,
  temperatureUnit: string,
) => {
  const label = getDailyLabel(entry, stationTimezone)
  const iconUrl = resolveIconUrl(entry.icon)
  const high = formatTemperature(entry.temp_max ?? entry.temp_day, temperatureUnit)
  const low = formatTemperature(entry.temp_min ?? entry.temp_night, temperatureUnit)
  const pop = formatPercent(entry.pop)

  return (
    <ListItem key={`${entry.date ?? index}-daily`} divider disablePadding>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 1,
          py: 1,
        }}
      >
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={entry.description ?? 'Weather condition'}
            width={48}
            height={48}
            loading="lazy"
          />
        ) : null}
        <ListItemText
          primary={label}
          secondary={entry.description ?? undefined}
          primaryTypographyProps={{ variant: 'subtitle1' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right' }}>
          POP {pop}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            minWidth: 96,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <span>{high}</span>
          <span aria-hidden="true">/</span>
          <span>{low}</span>
        </Typography>
      </Box>
    </ListItem>
  )
}

const formatAlertRange = (alert: WeatherAlert, stationTimezone: string | null): string => {
  const startEpoch = resolveEpochMs({ isoString: alert.start ?? null })
  const endEpoch = resolveEpochMs({ isoString: alert.end ?? null })
  const tz = resolveStationTimeZone(stationTimezone ?? undefined)

  if (startEpoch && endEpoch) {
    const startLabel = formatStationTime(startEpoch, tz, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
    const endLabel = formatStationTime(endEpoch, tz, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
    return `${startLabel} → ${endLabel}`
  }

  if (startEpoch) {
    return `From ${formatStationTime(startEpoch, tz, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })}`
  }

  if (endEpoch) {
    return `Until ${formatStationTime(endEpoch, tz, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })}`
  }

  return ''
}

const alertSeverityToMui: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  extreme: 'error',
  severe: 'error',
  moderate: 'warning',
  minor: 'info',
}

const formatMetadataBoolean = (value: boolean | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  return value ? 'Yes' : 'No'
}

const formatMetadataNumber = (
  value: number | null | undefined,
  options: { suffix?: string; precision?: number } = {},
): string | null => {
  if (!Number.isFinite(value ?? Number.NaN)) {
    return null
  }
  const numeric = Number(value)
  const precision = options.precision ?? (Math.abs(numeric) < 10 ? 2 : 0)
  const formatted = precision > 0 ? numeric.toFixed(precision) : Math.round(numeric).toString()
  return options.suffix ? `${formatted}${options.suffix}` : formatted
}

interface MetadataEntry {
  label: string
  value: React.ReactNode
}

const WeatherPanel: React.FC<WeatherPanelProps> = ({
  open,
  onClose,
  stationId,
  stationName,
  stationTimezone,
  dialogId,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const titleId = React.useId()
  const descriptionId = React.useId()

  const { data, loading, refreshing, hasFetched, error, manualRefresh } = useWeatherStore(
    React.useCallback((state) => {
      if (!stationId) {
        return {
          data: null,
          loading: false,
          refreshing: false,
          hasFetched: false,
          error: null,
          manualRefresh: null,
        }
      }
      const entry = state.stations[stationId]
      if (!entry) {
        return {
          data: null,
          loading: false,
          refreshing: false,
          hasFetched: false,
          error: null,
          manualRefresh: null,
        }
      }
      return {
        data: entry.data ?? null,
        loading: entry.loading ?? false,
        refreshing: entry.refreshing ?? false,
        hasFetched: entry.hasFetched ?? false,
        error: entry.error ? { message: entry.error.message } : null,
        manualRefresh: entry.manualRefresh
          ? {
              status: entry.manualRefresh.status,
              message: entry.manualRefresh.message,
              nextAllowedAt: entry.manualRefresh.nextAllowedAt,
              occurredAt: entry.manualRefresh.occurredAt,
            }
          : null,
      }
    }, [stationId]),
    shallow,
  )

  const fetchWeather = useWeatherStore((state) => state.fetch)
  const manualRefreshWeather = useWeatherStore((state) => state.manualRefresh)

  const hasData = Boolean(data)
  const isStale = Boolean(data?.stale?.is_stale)
  const summary = data?.report?.payload?.summary ?? null
  const hourlyRaw = data?.report?.payload?.hourly
  const dailyRaw = data?.report?.payload?.daily
  const alertsRaw = data?.report?.payload?.alerts
  const hourly = React.useMemo(
    () => (Array.isArray(hourlyRaw) ? hourlyRaw : []),
    [hourlyRaw],
  )
  const daily = React.useMemo(
    () => (Array.isArray(dailyRaw) ? dailyRaw : []),
    [dailyRaw],
  )
  const alerts = React.useMemo(
    () => (Array.isArray(alertsRaw) ? alertsRaw : []),
    [alertsRaw],
  )
  const timezone = stationTimezone ?? data?.report?.payload?.summary?.timezone ?? null
  const assistantAnswer = React.useMemo(() => {
    const answer = data?.report?.payload?.assistant_answer
    if (typeof answer !== 'string') {
      return null
    }
    const trimmed = answer.trim()
    return trimmed.length > 0 ? trimmed : null
  }, [data?.report?.payload?.assistant_answer])
  const assistantSession = data?.report?.payload?.assistant_session ?? null
  const sourceMetadata = data?.report?.payload?.source_metadata ?? null
  const locationLabel = data?.location ?? stationName ?? 'Weather'

  const lastUpdatedEpoch = React.useMemo(() => {
    if (data?.last_updated) {
      return resolveEpochMs({ isoString: data.last_updated })
    }
    const fetchedAt = data?.report?.payload?.fetched_at
    if (fetchedAt) {
      return resolveEpochMs({ isoString: fetchedAt })
    }
    return null
  }, [data?.last_updated, data?.report?.payload?.fetched_at])

  const lastUpdatedRelative = React.useMemo(() => {
    if (!Number.isFinite(lastUpdatedEpoch ?? Number.NaN)) {
      return null
    }
    return formatRelativeTime(lastUpdatedEpoch as number, timezone)
  }, [lastUpdatedEpoch, timezone])

  const manualCooldownSeconds = React.useMemo(() => {
    const nextAllowed = manualRefresh?.nextAllowedAt ?? data?.cooldown?.expires_at ?? null
    if (typeof nextAllowed === 'string') {
      const epoch = resolveEpochMs({ isoString: nextAllowed })
      if (Number.isFinite(epoch ?? Number.NaN)) {
        const diffMs = (epoch as number) - Date.now()
        if (diffMs > 0) {
          return Math.ceil(diffMs / 1000)
        }
      }
    }
    if (Number.isFinite(data?.cooldown?.remaining ?? Number.NaN)) {
      return Math.ceil(Number(data?.cooldown?.remaining))
    }
    if (Number.isFinite(data?.cooldown?.seconds ?? Number.NaN)) {
      return Math.ceil(Number(data?.cooldown?.seconds))
    }
    return 0
  }, [data?.cooldown?.expires_at, data?.cooldown?.remaining, data?.cooldown?.seconds, manualRefresh?.nextAllowedAt])

  const manualDisabledReason = React.useMemo(() => {
    if (!stationId) {
      return 'Select a station to view weather details.'
    }
    if (!data) {
      return 'Weather data is still loading.'
    }
    if (!data.enabled) {
      return 'Weather updates are disabled for this station.'
    }
    if (!data.manual_refresh.presenter_enabled) {
      return 'Presenters cannot request manual refreshes.'
    }
    if (!data.manual_refresh.allowed_for_user) {
      return 'You do not have permission to refresh weather.'
    }
    if (manualCooldownSeconds > 0) {
      return `Try again in ${formatCooldownLabel(manualCooldownSeconds)}`
    }
    return null
  }, [data, manualCooldownSeconds, stationId])

  React.useEffect(() => {
    if (!open || !stationId) {
      return
    }
    if (loading) {
      return
    }
    if (hasFetched && hasData && !isStale) {
      return
    }

    const controller = new AbortController()

    fetchWeather(stationId, { signal: controller.signal }).catch((fetchError) => {
      if ((import.meta as any)?.env?.DEV) {
        console.error('[RPA] Weather fetch failed', fetchError)
      }
    })

    return () => {
      controller.abort()
    }
  }, [open, stationId, fetchWeather, hasFetched, hasData, isStale, loading])

  const nextSixHours = React.useMemo(() => hourly.slice(0, 6), [hourly])

  const restOfDay = React.useMemo(() => {
    if (hourly.length <= 6) {
      return []
    }
    const baseline = hourly[0]
    const baselineEpoch = baseline ? resolveEpochMs({ isoString: baseline.time ?? null }) : null
    return hourly.slice(6).filter((entry) => {
      if (!baselineEpoch) {
        return true
      }
      const epoch = resolveEpochMs({ isoString: entry.time ?? null })
      if (!epoch) {
        return false
      }
      return stationDayDiff(epoch, baselineEpoch, timezone ?? undefined) === 0
    })
  }, [hourly, timezone])

  const manualAlertSeverity = manualRefresh?.status
    ? manualRefresh.status === 'success'
      ? 'success'
      : manualRefresh.status === 'cooldown'
        ? 'info'
        : 'error'
    : null

  const sessionMetadataEntries = React.useMemo<MetadataEntry[]>(() => {
    if (!assistantSession) {
      return []
    }

    const entries: MetadataEntry[] = []
    const tz = resolveStationTimeZone(timezone ?? undefined)

    const timestampEntries: Array<{ key: keyof WeatherAssistantSessionMetadata; label: string }> = [
      { key: 'created_at', label: 'Created at' },
      { key: 'started_at', label: 'Started at' },
      { key: 'refreshed_at', label: 'Refreshed at' },
      { key: 'finished_at', label: 'Finished at' },
      { key: 'expires_at', label: 'Expires at' },
    ]

    timestampEntries.forEach(({ key, label }) => {
      const value = assistantSession[key]
      if (typeof value === 'string' && value) {
        const epoch = resolveEpochMs({ isoString: value })
        if (Number.isFinite(epoch ?? Number.NaN)) {
          entries.push({
            label,
            value: formatStationTime(epoch as number, tz, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }),
          })
        } else {
          entries.push({ label, value })
        }
      }
    })

    const booleanEntries: Array<{ key: keyof WeatherAssistantSessionMetadata; label: string }> = [
      { key: 'cached', label: 'Cached response' },
    ]

    booleanEntries.forEach(({ key, label }) => {
      const formatted = formatMetadataBoolean(assistantSession[key] as boolean | null | undefined)
      if (formatted !== null) {
        entries.push({ label, value: formatted })
      }
    })

    const numericEntries: Array<{ key: keyof WeatherAssistantSessionMetadata; label: string; suffix?: string }> = [
      { key: 'prompt_tokens', label: 'Prompt tokens' },
      { key: 'completion_tokens', label: 'Completion tokens' },
      { key: 'total_tokens', label: 'Total tokens' },
      { key: 'duration_ms', label: 'Generation time', suffix: ' ms' },
      { key: 'http_status', label: 'Assistant HTTP status' },
    ]

    numericEntries.forEach(({ key, label, suffix }) => {
      const formatted = formatMetadataNumber(assistantSession[key] as number | null | undefined, {
        suffix,
        precision: suffix ? 0 : undefined,
      })
      if (formatted !== null) {
        entries.push({ label, value: formatted })
      }
    })

    const stringEntries: Array<{ key: keyof WeatherAssistantSessionMetadata; label: string }> = [
      { key: 'id', label: 'Session ID' },
      { key: 'request_id', label: 'Request ID' },
      { key: 'model', label: 'Model' },
      { key: 'finish_reason', label: 'Finish reason' },
      { key: 'cached_reason', label: 'Cache reason' },
    ]

    stringEntries.forEach(({ key, label }) => {
      const value = assistantSession[key]
      if (typeof value === 'string' && value.trim() !== '') {
        entries.push({ label, value })
      }
    })

    return entries
  }, [assistantSession, timezone])

  const sourceMetadataEntries = React.useMemo<MetadataEntry[]>(() => {
    if (!sourceMetadata) {
      return []
    }

    const entries: MetadataEntry[] = []

    const numericEntries: Array<{ key: keyof typeof sourceMetadata; label: string; suffix?: string; precision?: number }> = [
      { key: 'lat', label: 'Latitude', precision: 2 },
      { key: 'lon', label: 'Longitude', precision: 2 },
      { key: 'timezone_offset', label: 'Timezone offset', suffix: ' min' },
      { key: 'answer_length', label: 'Answer length', suffix: ' chars' },
    ]

    numericEntries.forEach(({ key, label, suffix, precision }) => {
      const value = sourceMetadata[key]
      if (Number.isFinite(value ?? Number.NaN)) {
        const formatted = formatMetadataNumber(value as number, { suffix, precision })
        if (formatted !== null) {
          entries.push({ label, value: formatted })
        }
      }
    })

    const stringEntries: Array<{ key: keyof typeof sourceMetadata; label: string }> = [
      { key: 'resolved_location', label: 'Resolved location' },
      { key: 'location_input', label: 'Location input' },
      { key: 'units', label: 'Units' },
      { key: 'units_label', label: 'Units label' },
      { key: 'data_source', label: 'Data source' },
      { key: 'data_version', label: 'Data version' },
      { key: 'endpoint', label: 'API endpoint' },
      { key: 'assistant_endpoint', label: 'Assistant endpoint' },
      { key: 'prompt', label: 'Prompt' },
      { key: 'timezone', label: 'Source timezone' },
      { key: 'session_id', label: 'Assistant session ID' },
    ]

    stringEntries.forEach(({ key, label }) => {
      const value = sourceMetadata[key]
      if (typeof value === 'string' && value.trim() !== '') {
        entries.push({ label, value })
      }
    })

    if (sourceMetadata.data_keys?.length) {
      entries.push({ label: 'Data keys', value: sourceMetadata.data_keys.join(', ') })
    }

    return entries
  }, [sourceMetadata])

  const handleManualRefresh = React.useCallback(() => {
    if (!stationId || manualDisabledReason || refreshing) {
      return
    }
    manualRefreshWeather(stationId).catch((refreshError) => {
      if ((import.meta as any)?.env?.DEV) {
        console.error('[RPA] Manual weather refresh failed', refreshError)
      }
    })
  }, [manualDisabledReason, manualRefreshWeather, refreshing, stationId])

  const renderContent = () => {
    if (!stationId) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <CloudOffIcon fontSize="large" color="disabled" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Select a station to view weather
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose a station to unlock the local weather assistant.
          </Typography>
        </Paper>
      )
    }

    if (loading && !hasData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading weather" />
        </Box>
      )
    }

    if (error && !hasData) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchWeather(stationId)}>
              Retry
            </Button>
          }
        >
          {error.message}
        </Alert>
      )
    }

    if (!data) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <CloudOffIcon fontSize="large" color="disabled" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Weather data unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            We have not received any weather reports yet. Try refreshing to fetch the latest update.
          </Typography>
        </Paper>
      )
    }

    if (!data.enabled) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <CloudOffIcon fontSize="large" color="disabled" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Weather is disabled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enable weather updates in station settings to see forecasts here.
          </Typography>
        </Paper>
      )
    }

    if (!data.report || !data.report.payload) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <WarningIcon fontSize="large" color="warning" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No active forecast
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The latest weather run has not completed yet. Request a manual refresh if needed.
          </Typography>
        </Paper>
      )
    }

    const temperatureUnit =
      summary?.temperature_unit ?? (data.units === 'imperial' ? '°F' : data.units === 'metric' ? '°C' : '°')
    const windSystem = data.units ?? null

    return (
      <Stack spacing={WEATHER_SECTION_GAP} sx={{ pt: 0.5 }}>
        {summary ? (
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {(() => {
                    const summaryIconUrl = resolveIconUrl(summary.icon)
                    if (!summaryIconUrl) {
                      return <WbSunnyIcon fontSize="large" color="primary" />
                    }
                    return (
                      <img
                        src={summaryIconUrl}
                        alt={summary.description ?? 'Current weather icon'}
                        width={80}
                        height={80}
                        loading="lazy"
                      />
                    )
                  })()}
                  <Box>
                    <Typography variant="h3" component="p">
                      {formatTemperature(summary.temperature, temperatureUnit)}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {summary.description ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Feels like {formatFeelsLike(summary.feels_like, temperatureUnit)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
              <GridSummary summary={summary} timezone={timezone} temperatureUnit={temperatureUnit} windSystem={windSystem} />
            </Stack>
          </Paper>
        ) : null}

        {assistantAnswer ? (
          <SectionPaper title="Assistant answer" description="Ready-to-read script">
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {assistantAnswer}
            </Typography>
          </SectionPaper>
        ) : null}

        <SectionPaper title="Next 6 hours" description="Short-term outlook" role="list">
          {nextSixHours.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(180px, 1fr))',
                },
                gap: 2,
              }}
            >
              {nextSixHours.map((entry, index) =>
                renderHourlyCard(entry, timezone, index, temperatureUnit, windSystem),
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Hourly breakdown is not available yet.
            </Typography>
          )}
        </SectionPaper>

        <SectionPaper title="Rest of day" description="Stay ahead for later today" role="list">
          {restOfDay.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(160px, 1fr))',
                  lg: 'repeat(auto-fill, minmax(180px, 1fr))',
                },
                gap: 2,
              }}
            >
              {restOfDay.map((entry, index) =>
                renderHourlyCard(entry, timezone, index, temperatureUnit, windSystem),
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You are all caught up for today.
            </Typography>
          )}
        </SectionPaper>

        <SectionPaper title="Multi-day outlook" description="Plan upcoming shows" role="list">
          {daily.length > 0 ? (
            <List sx={{ width: '100%' }}>
              {daily.slice(0, 6).map((entry, index) =>
                renderDailyItem(entry, timezone, index, temperatureUnit),
              )}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Daily forecast is not available yet.
            </Typography>
          )}
        </SectionPaper>

        <SectionPaper title="Weather alerts" description="Critical updates" role="list">
          {alerts.length > 0 ? (
            <Stack spacing={1.5} role="list">
              {alerts.map((alert, index) => {
                const severity =
                  (alert.severity && alertSeverityToMui[alert.severity.toLowerCase()]) || 'warning'
                const range = formatAlertRange(alert, timezone)
                return (
                  <Alert
                    key={`${alert.event ?? index}-alert`}
                    icon={<WarningIcon fontSize="small" />}
                    severity={severity}
                    role="listitem"
                  >
                    <Typography variant="subtitle1" component="p">
                      {alert.event ?? 'Weather alert'}
                    </Typography>
                    {range ? (
                      <Typography variant="body2" color="text.secondary">
                        {range}
                      </Typography>
                    ) : null}
                    {alert.description ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {alert.description}
                      </Typography>
                    ) : null}
                    {alert.sender ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Source: {alert.sender}
                      </Typography>
                    ) : null}
                  </Alert>
                )
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No active alerts.
            </Typography>
          )}
        </SectionPaper>

        {sessionMetadataEntries.length > 0 || sourceMetadataEntries.length > 0 ? (
          <SectionPaper title="Assistant metadata" description="Trace the forecast">
            <Stack spacing={2}>
              {sessionMetadataEntries.length > 0 ? (
                <MetadataGrid entries={sessionMetadataEntries} />
              ) : null}
              {sourceMetadataEntries.length > 0 ? (
                <MetadataGrid entries={sourceMetadataEntries} />
              ) : null}
            </Stack>
          </SectionPaper>
        ) : null}
      </Stack>
    )
  }

  return (
    <Dialog
      id={dialogId}
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      scroll="paper"
    >
      <DialogTitle id={titleId} sx={{ pb: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WbSunnyIcon color="primary" />
            <Box>
              <Typography variant="h6">Weather assistant</Typography>
              <Typography variant="body2" color="text.secondary">
                {locationLabel}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {lastUpdatedRelative ? (
              <Typography
                variant="body2"
                color="text.secondary"
                title={lastUpdatedRelative.tooltip}
                aria-live="polite"
              >
                Last updated {lastUpdatedRelative.label.replace(/^·/, '')}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Last updated —
              </Typography>
            )}
            {manualCooldownSeconds > 0 ? (
              <Chip
                size="small"
                color="warning"
                label={`Cooldown ${formatCooldownLabel(manualCooldownSeconds)}`}
                aria-live="polite"
              />
            ) : null}
            <Tooltip title={manualDisabledReason ?? 'Request a fresh forecast'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
                  }
                  onClick={handleManualRefresh}
                  disabled={Boolean(manualDisabledReason) || refreshing}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} aria-label="Close weather assistant">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent id={descriptionId} dividers sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {data?.stale?.is_stale ? (
            <Alert severity="warning" role="status">
              <Typography variant="body2">
                Latest automated run is stale: {data.stale.reason ?? 'Awaiting refresh'}
              </Typography>
            </Alert>
          ) : null}
          {manualAlertSeverity && manualRefresh?.message ? (
            <Alert severity={manualAlertSeverity} role="status">
              <Typography variant="body2">{manualRefresh.message}</Typography>
              {manualRefresh.occurredAt ? (
                <Typography variant="caption" color="text.secondary">
                  {formatRelativeTime(manualRefresh.occurredAt, timezone).label.replace(/^·/, '')}
                </Typography>
              ) : null}
            </Alert>
          ) : null}
          {renderContent()}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          Manual refreshes respect presenter permissions and global cooldown policies.
        </Typography>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface GridSummaryProps {
  summary: NonNullable<WeatherState['report']>['payload']['summary']
  timezone: string | null
  temperatureUnit: string
  windSystem: string | null
}

const GridSummary: React.FC<GridSummaryProps> = ({ summary, timezone, temperatureUnit, windSystem }) => {
  const sunriseEpoch = resolveEpochMs({ isoString: summary.sunrise ?? null })
  const sunsetEpoch = resolveEpochMs({ isoString: summary.sunset ?? null })
  const tz = resolveStationTimeZone(timezone ?? undefined)

  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 2,
        width: '100%',
      }}
    >
      <Definition label="Humidity" value={formatPercent(summary.humidity)} />
      <Definition label="Pressure" value={summary.pressure ? `${Math.round(summary.pressure)} hPa` : '—'} />
      <Definition label="Dew point" value={formatTemperature(summary.dew_point, temperatureUnit)} />
      <Definition label="UV index" value={summary.uvi !== null && summary.uvi !== undefined ? `${summary.uvi}` : '—'} />
      <Definition
        label="Visibility"
        value={summary.visibility ? `${Math.round(summary.visibility / 1000)} km` : '—'}
      />
      <Definition label="Wind" value={formatWind(summary.wind_speed, windSystem, summary.wind_deg)} />
      <Definition
        label="Sunrise"
        value={
          sunriseEpoch
            ? formatStationTime(sunriseEpoch, tz, { hour: 'numeric', minute: '2-digit' })
            : '—'
        }
      />
      <Definition
        label="Sunset"
        value={
          sunsetEpoch ? formatStationTime(sunsetEpoch, tz, { hour: 'numeric', minute: '2-digit' }) : '—'
        }
      />
    </Box>
  )
}

interface DefinitionProps {
  label: string
  value: React.ReactNode
}

const Definition: React.FC<DefinitionProps> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
)

interface MetadataGridProps {
  entries: MetadataEntry[]
}

const MetadataGrid: React.FC<MetadataGridProps> = ({ entries }) => {
  if (!entries.length) {
    return null
  }

  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
        width: '100%',
      }}
    >
      {entries.map((entry, index) => (
        <Definition key={`${entry.label}-${index}`} label={entry.label} value={entry.value} />
      ))}
    </Box>
  )
}

interface SectionPaperProps {
  title: string
  description: string
  children: React.ReactNode
  role?: string
}

const SectionPaper: React.FC<SectionPaperProps> = ({ title, description, children, role }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Box role={role} aria-label={title} sx={{ width: '100%' }}>
        {children}
      </Box>
    </Stack>
  </Paper>
)

export default WeatherPanel
